// Values over time, one y-axis (interface refresh charts; method from the data-viz guide):
// 1–4 lines in the validated series colours (a 4th reuses colour 1, dashed), an optional
// allowed range drawn as a band, gaps where no data arrived, and a crosshair that reads every
// series at that moment (mouse, touch and arrow keys). Values are also written out beside the
// chart (now / low / high), so nothing depends on colour or hovering. Copied in web/ and crm/.
import { KeyboardEvent, PointerEvent, useEffect, useMemo, useRef, useState } from 'react';

export interface Series {
  name: string;
  points: Array<[number, number]>;   // [time ms, value], in time order
  ranged?: boolean;                  // the allowed range applies to it (default: yes)
}

const H = 190;
const PAD = { l: 46, r: 12, t: 12, b: 26 };
const tz = 'Europe/Dublin';
const fmtTime = new Intl.DateTimeFormat('en-IE', { hour: '2-digit', minute: '2-digit', timeZone: tz });
const fmtDay = new Intl.DateTimeFormat('en-IE', { weekday: 'short', day: 'numeric', timeZone: tz });
const fmtFull = new Intl.DateTimeFormat('en-IE', { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', timeZone: tz });

// Round axis steps: 1, 2, 2.5, 5 × 10^n.
function niceStep(span: number, count: number): number {
  const raw = span / Math.max(1, count);
  const p = 10 ** Math.floor(Math.log10(raw || 1));
  for (const m of [1, 2, 2.5, 5, 10]) if (m * p >= raw) return m * p;
  return 10 * p;
}

export function TimeChart({ title, series, from, to, unit, limits, yMin, yMax, step = false, decimals = 1, gapMs, empty }: {
  title: string;
  series: Series[];
  from: number;
  to: number;
  unit: string;
  limits?: { min?: number; max?: number } | null;
  yMin?: number;            // fixed axis ends, e.g. GSM signal 0–5
  yMax?: number;
  step?: boolean;           // hold each value until the next (levels such as signal bars)
  decimals?: number;
  gapMs?: number;           // a longer silence breaks the line (default: 6 % of the range, at least 20 min)
  empty?: string;
}) {
  const box = useRef<HTMLDivElement>(null);
  const [W, setW] = useState(640);
  useEffect(() => {
    const el = box.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setW(Math.max(260, Math.round(el.clientWidth))));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const [cursor, setCursor] = useState<number | null>(null);   // time ms under the crosshair

  const all = series.flatMap((s) => s.points.map((p) => p[1]));
  const { lo, hi, ticks } = useMemo(() => {
    let lo = Math.min(...all, limits?.min ?? Infinity);
    let hi = Math.max(...all, limits?.max ?? -Infinity);
    if (!Number.isFinite(lo) || !Number.isFinite(hi)) { lo = 0; hi = 1; }
    if (yMin !== undefined) lo = yMin;
    if (yMax !== undefined) hi = yMax;
    if (hi - lo < 1e-9) { lo -= 1; hi += 1; }
    const st = niceStep(hi - lo, 4);
    if (yMin === undefined) lo = Math.floor(lo / st) * st;
    if (yMax === undefined) hi = Math.ceil(hi / st) * st;
    const ticks: number[] = [];
    for (let v = lo; v <= hi + st / 1000; v += st) ticks.push(Number(v.toFixed(6)));
    return { lo, hi, ticks };
  }, [all.join(','), limits?.min, limits?.max, yMin, yMax]); // eslint-disable-line react-hooks/exhaustive-deps

  const x = (t: number) => PAD.l + ((t - from) / (to - from)) * (W - PAD.l - PAD.r);
  const y = (v: number) => PAD.t + (1 - (v - lo) / (hi - lo)) * (H - PAD.t - PAD.b);
  const gap = gapMs ?? Math.max(20 * 60_000, (to - from) * 0.06);

  const path = (pts: Array<[number, number]>) => {
    let d = '';
    pts.forEach(([t, v], i) => {
      const prev = pts[i - 1];
      const broken = !prev || t - prev[0] > gap;
      if (broken) d += `M${x(t).toFixed(1)},${y(v).toFixed(1)}`;
      else if (step) d += `H${x(t).toFixed(1)}V${y(v).toFixed(1)}`;
      else d += `L${x(t).toFixed(1)},${y(v).toFixed(1)}`;
    });
    return d;
  };

  // Time labels: hours within two days, days beyond.
  const days = (to - from) / 86_400_000;
  const xTicks: number[] = [];
  const stepH = days <= 1.1 ? (W > 560 ? 3 : 6) : days <= 2.2 ? 12 : 24 * Math.ceil(days / (W > 560 ? 8 : 4));
  const first = Math.ceil(from / (stepH * 3600_000)) * stepH * 3600_000;
  for (let t = first; t <= to; t += stepH * 3600_000) xTicks.push(t);
  const xLabel = (t: number) => (days <= 2.2 ? fmtTime.format(t) : fmtDay.format(t));

  const fmt = (v: number) => `${v.toLocaleString('en-IE', { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}${unit ? ` ${unit}` : ''}`;
  const nearest = (s: Series, t: number) => {
    let best: [number, number] | null = null;
    for (const p of s.points) if (!best || Math.abs(p[0] - t) < Math.abs(best[0] - t)) best = p;
    return best && Math.abs(best[0] - t) <= gap ? best : null;
  };
  const times = useMemo(() => [...new Set(series.flatMap((s) => s.points.map((p) => p[0])))].sort((a, b) => a - b), [series]);
  const snap = (t: number) => times.reduce((b, c) => (Math.abs(c - t) < Math.abs(b - t) ? c : b), times[0]);

  const onMove = (e: PointerEvent<SVGRectElement>) => {
    if (!times.length) return;
    const r = e.currentTarget.getBoundingClientRect();
    const t = from + ((e.clientX - r.left) / r.width) * (to - from);
    setCursor(snap(t));
  };
  const onKey = (e: KeyboardEvent<SVGSVGElement>) => {
    if (!times.length) return;
    const i = cursor === null ? times.length - 1 : times.indexOf(cursor);
    if (e.key === 'ArrowLeft') { setCursor(times[Math.max(0, i - 1)]); e.preventDefault(); }
    else if (e.key === 'ArrowRight') { setCursor(times[Math.min(times.length - 1, i + 1)]); e.preventDefault(); }
    else if (e.key === 'Escape') setCursor(null);
  };

  const readout = cursor === null ? null : series.map((s, i) => ({ s, i, p: nearest(s, cursor) })).filter((r) => r.p);
  const tipLeft = cursor === null ? 0 : Math.min(Math.max(x(cursor) / W, 0.12), 0.88) * 100;
  const out = (v: number) => (limits?.min !== undefined && v < limits.min) || (limits?.max !== undefined && v > limits.max);

  return (
    <figure className="tchart">
      <figcaption>{title}</figcaption>
      {series.every((s) => s.points.length === 0) ? <p className="muted small tchart-empty">{empty ?? 'No data in this period.'}</p> : (
        <>
          <div className="tchart-box" ref={box}>
            <svg viewBox={`0 0 ${W} ${H}`} role="img" tabIndex={0} onKeyDown={onKey} onBlur={() => setCursor(null)}
              aria-label={`${title}, ${fmtFull.format(from)} to ${fmtFull.format(to)}. Use the left and right arrow keys to read values.`}>
              {limits && (limits.min !== undefined || limits.max !== undefined) && (
                <g className="tchart-limits">
                  <rect x={PAD.l} width={W - PAD.l - PAD.r}
                    y={y(Math.min(hi, limits.max ?? hi))} height={Math.max(0, y(Math.max(lo, limits.min ?? lo)) - y(Math.min(hi, limits.max ?? hi)))} />
                  {limits.max !== undefined && limits.max <= hi && <line x1={PAD.l} x2={W - PAD.r} y1={y(limits.max)} y2={y(limits.max)} />}
                  {limits.min !== undefined && limits.min >= lo && <line x1={PAD.l} x2={W - PAD.r} y1={y(limits.min)} y2={y(limits.min)} />}
                </g>
              )}
              {ticks.map((v) => (
                <g key={v}>
                  <line className="tchart-grid" x1={PAD.l} x2={W - PAD.r} y1={y(v)} y2={y(v)} />
                  <text className="tchart-tick" x={PAD.l - 6} y={y(v) + 4} textAnchor="end">{v.toLocaleString('en-IE', { maximumFractionDigits: 2 })}</text>
                </g>
              ))}
              {xTicks.map((t) => <text key={t} className="tchart-tick" x={x(t)} y={H - 6} textAnchor="middle">{xLabel(t)}</text>)}
              {series.map((s, i) => (
                <path key={s.name} className={`tchart-line s${(i % 3) + 1}${i >= 3 ? ' dashed' : ''}`} d={path(s.points)} />
              ))}
              {/* Readings outside the allowed range get a red ring (also named in the readout). */}
              {limits && series.filter((s) => s.ranged !== false).map((s) => s.points.filter((p) => out(p[1])).slice(0, 400).map((p) => (
                <circle key={`${s.name}${p[0]}`} className="tchart-out" cx={x(p[0])} cy={y(p[1])} r={2.5} />
              )))}
              {cursor !== null && (
                <g className="tchart-cursor">
                  <line x1={x(cursor)} x2={x(cursor)} y1={PAD.t} y2={H - PAD.b} />
                  {readout?.map((r) => <circle key={r.s.name} className={`s${(r.i % 3) + 1}`} cx={x(r.p![0])} cy={y(r.p![1])} r={4} />)}
                </g>
              )}
              <rect className="tchart-hit" x={PAD.l} y={PAD.t} width={W - PAD.l - PAD.r} height={H - PAD.t - PAD.b}
                onPointerMove={onMove} onPointerDown={onMove} onPointerLeave={() => setCursor(null)} />
            </svg>
            {readout && readout.length > 0 && (
              <div className="tchart-tip" style={{ left: `${tipLeft}%` }} role="status">
                <div className="tchart-tip-time">{fmtFull.format(cursor!)}</div>
                {readout.map((r) => (
                  <div key={r.s.name} className="tchart-tip-row">
                    <span className={`tchart-key s${(r.i % 3) + 1}${r.i >= 3 ? ' dashed' : ''}`} aria-hidden="true" />
                    <strong>{fmt(r.p![1])}</strong>{series.length > 1 && <span className="muted"> {r.s.name}</span>}
                    {r.s.ranged !== false && out(r.p![1]) && <span className="tchart-tip-out"> outside range</span>}
                  </div>
                ))}
              </div>
            )}
          </div>
          {/* Legend and values in text: identity never by colour alone. */}
          <ul className="tchart-legend">
            {series.map((s, i) => {
              const vs = s.points.map((p) => p[1]);
              const last = s.points[s.points.length - 1];
              return (
                <li key={s.name}>
                  {series.length > 1 && <span className={`tchart-key s${(i % 3) + 1}${i >= 3 ? ' dashed' : ''}`} aria-hidden="true" />}
                  {series.length > 1 && <span className="tchart-name">{s.name}</span>}
                  {last ? <span className="muted">now <b>{fmt(last[1])}</b> · low {fmt(Math.min(...vs))} · high {fmt(Math.max(...vs))}</span> : <span className="muted">no readings</span>}
                </li>
              );
            })}
            {limits && (limits.min !== undefined || limits.max !== undefined) && (
              <li className="muted"><span className="tchart-key band" aria-hidden="true" />allowed {limits.min !== undefined ? fmt(limits.min) : '…'} to {limits.max !== undefined ? fmt(limits.max) : '…'}
                {series.some((s) => s.ranged === false) && <> ({series.filter((s) => s.ranged !== false).map((s) => s.name).join(', ')})</>}</li>
            )}
          </ul>
        </>
      )}
    </figure>
  );
}
